<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import CsatMetricCard from './CsatMetricCard.vue';

const props = defineProps({
  // Meta configurada em Configurações → Pesquisa de Satisfação (0-100)
  targetScore: { type: Number, default: null },
});

const { t } = useI18n();
const nps = useMapGetter('csat/getNpsMetrics');
const uiFlags = useMapGetter('csat/getUIFlags');

const isLoading = computed(() => uiFlags.value.isFetchingMetrics);
const formatNumber = v => Number(v || 0).toLocaleString('pt-BR');

const pct = (numerator, denominator) => {
  if (!denominator) return 0;
  return ((numerator / denominator) * 100).toFixed(1);
};

const totalResponses = computed(() => nps.value?.total || 0);
const npsScore = computed(() => nps.value?.score ?? 0);
const breakdown = computed(
  () => nps.value?.breakdown || { promoter: 0, passive: 0, detractor: 0 }
);
const promoterPct = computed(() =>
  pct(breakdown.value.promoter, totalResponses.value)
);
const passivePct = computed(() =>
  pct(breakdown.value.passive, totalResponses.value)
);
const detractorPct = computed(() =>
  pct(breakdown.value.detractor, totalResponses.value)
);

const targetStatus = computed(() => {
  if (props.targetScore == null) return null;
  const score = Number(npsScore.value) || 0;
  const target = props.targetScore;
  if (score >= target)
    return {
      color: 'text-n-grass-11',
      icon: 'i-lucide-check',
      label: t('CSAT_REPORTS.METRIC.TARGET.MET', { target }),
    };
  if (score >= target - 10)
    return {
      color: 'text-n-amber-11',
      icon: 'i-lucide-trending-up',
      label: t('CSAT_REPORTS.METRIC.TARGET.NEAR', {
        gap: (target - score).toFixed(0),
        target,
      }),
    };
  return {
    color: 'text-n-ruby-11',
    icon: 'i-lucide-trending-down',
    label: t('CSAT_REPORTS.METRIC.TARGET.BELOW', { target }),
  };
});

const npsScoreColor = computed(() => {
  const score = Number(npsScore.value) || 0;
  if (score >= 50) return 'text-n-grass-11';
  if (score >= 0) return 'text-n-amber-11';
  return 'text-n-ruby-11';
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      class="flex sm:flex-row flex-col w-full gap-4 sm:gap-14 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
    >
      <CsatMetricCard
        :label="t('NPS_REPORTS.METRIC.TOTAL_RESPONSES')"
        :tooltip="t('NPS_REPORTS.METRIC.TOTAL_RESPONSES_TOOLTIP')"
        :value="formatNumber(totalResponses)"
        :is-loading="isLoading"
      />

      <div class="w-full sm:w-px bg-n-strong" />

      <div class="flex-1">
        <CsatMetricCard
          :label="t('NPS_REPORTS.METRIC.SCORE')"
          :tooltip="t('NPS_REPORTS.METRIC.SCORE_TOOLTIP')"
          :value="npsScore"
          :value-class="npsScoreColor"
          :is-loading="isLoading"
        />
        <p
          v-if="targetStatus && !isLoading"
          class="mt-1.5 inline-flex items-center gap-1 text-body-small"
          :class="targetStatus.color"
        >
          <i :class="targetStatus.icon" class="size-3.5" />
          {{ targetStatus.label }}
        </p>
      </div>

      <div class="w-full sm:w-px bg-n-strong" />

      <CsatMetricCard
        :label="t('NPS_REPORTS.METRIC.PROMOTERS')"
        :tooltip="t('NPS_REPORTS.METRIC.PROMOTERS_TOOLTIP')"
        :value="`${promoterPct}%`"
        :is-loading="isLoading"
      />
    </div>

    <!-- Breakdown promoter/passive/detractor -->
    <div
      class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
    >
      <h3 class="text-heading-3 text-n-slate-12 mb-3">
        {{ t('NPS_REPORTS.BREAKDOWN.TITLE') }}
      </h3>

      <div v-if="totalResponses === 0" class="text-body-main text-n-slate-11">
        {{ t('NPS_REPORTS.EMPTY') }}
      </div>

      <div v-else class="flex flex-col gap-3">
        <!-- Barra visual: detractor (red) | passive (amber) | promoter (green) -->
        <div class="flex h-3 w-full overflow-hidden rounded-md bg-n-alpha-2">
          <div class="bg-n-ruby-9" :style="{ width: `${detractorPct}%` }" />
          <div class="bg-n-amber-9" :style="{ width: `${passivePct}%` }" />
          <div class="bg-n-grass-9" :style="{ width: `${promoterPct}%` }" />
        </div>

        <div class="grid grid-cols-3 gap-3 text-body-main">
          <div class="flex items-start gap-2">
            <span class="size-2.5 rounded-full bg-n-ruby-9 mt-1.5" />
            <div>
              <p class="font-semibold text-n-slate-12 mb-0">
                {{ t('NPS_REPORTS.BREAKDOWN.DETRACTORS') }}
              </p>
              <p class="text-body-small text-n-slate-11 mb-0">
                {{ formatNumber(breakdown.detractor) }} · {{ detractorPct }}%
              </p>
              <p class="text-label-small text-n-slate-10 mb-0">
                {{ t('NPS_REPORTS.BREAKDOWN.DETRACTORS_HINT') }}
              </p>
            </div>
          </div>
          <div class="flex items-start gap-2">
            <span class="size-2.5 rounded-full bg-n-amber-9 mt-1.5" />
            <div>
              <p class="font-semibold text-n-slate-12 mb-0">
                {{ t('NPS_REPORTS.BREAKDOWN.PASSIVES') }}
              </p>
              <p class="text-body-small text-n-slate-11 mb-0">
                {{ formatNumber(breakdown.passive) }} · {{ passivePct }}%
              </p>
              <p class="text-label-small text-n-slate-10 mb-0">
                {{ t('NPS_REPORTS.BREAKDOWN.PASSIVES_HINT') }}
              </p>
            </div>
          </div>
          <div class="flex items-start gap-2">
            <span class="size-2.5 rounded-full bg-n-grass-9 mt-1.5" />
            <div>
              <p class="font-semibold text-n-slate-12 mb-0">
                {{ t('NPS_REPORTS.BREAKDOWN.PROMOTERS') }}
              </p>
              <p class="text-body-small text-n-slate-11 mb-0">
                {{ formatNumber(breakdown.promoter) }} · {{ promoterPct }}%
              </p>
              <p class="text-label-small text-n-slate-10 mb-0">
                {{ t('NPS_REPORTS.BREAKDOWN.PROMOTERS_HINT') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
